import { mat4, ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import {
    buildSnappedGrid,
    clampTranslationToContent,
    StructureTransformAction,
    TransformedStructure,
} from './transformUtils';

/**
 * Action that handles interactive translation (dragging) of a structure's
 * SDF texture by shifting the texture grid origin.
 *
 * During drag only the grid origin is updated for smooth visual feedback.
 * On release the SDF pixels are resampled onto a grid snapped to the
 * image voxel lattice so the contours match what the user saw during
 * the drag without sub-voxel misalignment.
 *
 * When {@link _siblings} is non-empty, the same translation delta is
 * applied to each sibling support structure.
 * The delta stays within the bounds each structure's image sets it
 * ({@link clampTranslationToContent}); all commit together or not at all.
 */
export class StructureTranslateAction extends StructureTransformAction {
    private readonly _roi: RegionOfInterest;
    private readonly _siblings: readonly RegionOfInterest[];

    /** The view in which the drag started — all coordinate
     *  conversions must use this view to avoid cross-view
     *  projection mismatches. */
    private _startView?: PlaneView;
    private _startPatient?: ReadonlyVec3;

    constructor(roi: RegionOfInterest, siblings?: readonly RegionOfInterest[]) {
        super();
        this._roi = roi;
        this._siblings = siblings ?? [];
    }

    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const structure = TransformedStructure.of(this._roi);
        if (!structure) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        this._startView = view;
        this._startPatient = view.clientToPatientCoordinates(coordinates);

        // Snapshot sibling grids and pixels
        const siblings = this._siblings.flatMap(
            (sib) => TransformedStructure.of(sib) ?? []
        );
        this.structures = [structure, ...siblings];

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        if (
            !this._startView ||
            !this._startPatient ||
            this.structures.length === 0
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // Defer startModify until the first actual drag
        const bounds = this.structures.map((s) => s.start('translate'));

        // Always use the view where the drag started so the
        // coordinate frame is consistent across the whole gesture.
        const currentPatient =
            this._startView.clientToPatientCoordinates(coordinates);
        const delta = vec3.subtract(
            vec3.create(),
            currentPatient,
            this._startPatient
        );

        const clampedDelta = clampTranslationToContent(delta, bounds, [
            this._startView.viewXDirection,
            this._startView.viewYDirection,
        ]);
        const clampedMat = mat4.fromTranslation(mat4.create(), clampedDelta);
        for (const s of this.structures) {
            s.texture.resetGrid(s.grid.transform(clampedMat));
        }

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2
    ): EventHandlerResult {
        // Resample the SDF onto a grid snapped to the image voxel
        // lattice. This preserves the contour shape the user saw
        // during the drag while avoiding sub-voxel misalignment
        // that would degrade downstream operations (boolean ops,
        // margins, dose mapping).
        this.commitStructures(({ texture }) => ({
            srcGrid: texture.textureGrid,
            dstGrid: buildSnappedGrid(texture.textureGrid, texture.grid),
        }));

        this._startView = undefined;
        this._startPatient = undefined;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }
}
